import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Activity, User, Search, Loader2, Check, XCircle } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Cube3D } from "../components/Cube3D";
import { StatusBadge } from "../components/StatusBadge";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { api, fileUrl, STATUS_COLORS, STATUSES } from "../lib/api";
import { ClientPushButton } from "../components/ClientPushButton";
import { playChime } from "../lib/sound";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};

const StatusStepper = ({ current }) => {
  const idx = STATUSES.indexOf(current);
  return (
    <div data-testid="status-stepper" className="mt-5 overflow-x-auto pb-1">
      <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-4">Fasi di lavorazione</p>
      <div className="flex items-start min-w-[360px]">
        {STATUSES.map((s, i) => {
          const col = STATUS_COLORS[s] || "#00F0FF";
          const done = idx >= 0 && i < idx;
          const active = i === idx;
          const reached = done || active;
          return (
            <div key={s} className="flex-1 flex flex-col items-center relative">
              {i < STATUSES.length - 1 && (
                <span className="absolute top-3.5 left-1/2 w-full h-0.5 rounded-full"
                  style={{ backgroundColor: done ? col : "rgba(255,255,255,0.12)" }} />
              )}
              <span data-testid={`stepper-dot-${i}`}
                className={`relative z-10 w-7 h-7 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${active ? "animate-pulse-glow" : ""}`}
                style={{
                  borderColor: reached ? col : "rgba(255,255,255,0.25)",
                  backgroundColor: reached ? col : "#0D1117",
                  boxShadow: active ? `0 0 12px ${col}` : "none",
                }}>
                {done
                  ? <Check className="w-4 h-4" style={{ color: "#000" }} />
                  : <span className="w-2 h-2 rounded-full" style={{ backgroundColor: active ? "#000" : "rgba(255,255,255,0.4)" }} />}
              </span>
              <span className="mt-2 px-0.5 text-[10px] font-mono text-center leading-tight" style={{ color: reached ? col : "#8B949E" }}>{s}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default function StatusPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [boardOrder, setBoardOrder] = useState("asc");
  const [code, setCode] = useState("");
  const [tracked, setTracked] = useState(null);
  const [trackErr, setTrackErr] = useState("");
  const [tracking, setTracking] = useState(false);
  const prevStatusRef = useRef(null);
  const [cancelling, setCancelling] = useState(false);
  const cancelOrder = async () => {
    if (!window.confirm("Vuoi davvero annullare questo ordine? L'operazione non è reversibile.")) return;
    setCancelling(true);
    try {
      await api.post(`/requests/cancel/${tracked.order_code}`);
      toast.success("Ordine annullato correttamente");
      setTracked(null); setCode("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile annullare l'ordine");
    } finally { setCancelling(false); }
  };

  useEffect(() => {
    if (!tracked?.status) return;
    if (prevStatusRef.current && prevStatusRef.current !== tracked.status) {
      playChime();
      toast(`Aggiornamento ordine #${tracked.order_code}`, { description: `Nuovo stato: ${tracked.status}`, duration: 6000 });
    }
    prevStatusRef.current = tracked.status;
  }, [tracked?.status, tracked?.order_code]);

  useEffect(() => {
    if (!tracked?.order_code) return;
    const id = setInterval(async () => {
      try { const r = await api.get(`/requests/track/${tracked.order_code}`); setTracked(r.data); } catch {}
    }, 20000);
    return () => clearInterval(id);
  }, [tracked?.order_code]);

  useEffect(() => {
    api.get("/requests/public", { params: { order: boardOrder } }).then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, [boardOrder]);

  const track = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setTracking(true); setTrackErr(""); setTracked(null);
    try {
      const r = await api.get(`/requests/track/${code.trim()}`);
      setTracked(r.data);
    } catch {
      setTrackErr("Nessun ordine trovato con questo codice.");
    } finally { setTracking(false); }
  };

  useEffect(() => {
    const c = new URLSearchParams(window.location.search).get("code");
    if (c) {
      setCode(c);
      api.get(`/requests/track/${c.trim()}`).then((r) => setTracked(r.data)).catch(() => setTrackErr("Nessun ordine trovato con questo codice."));
    }
  }, []);

  return (
    <div className="noise-overlay min-h-screen">
      <Navbar />
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16 relative">
        <Cube3D className="hidden md:block absolute top-12 right-2 opacity-60" size={84} variant="cyan" />
        <span className="font-mono text-xs uppercase tracking-widest text-neon-yellow" style={{ color: "#FFE600" }}>Bacheca lavori</span>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-white mt-2">
          Stato <span className="text-neon-cyan drop-shadow-[0_0_14px_rgba(0,240,255,0.5)]">avanzamento</span>
        </h1>
        <p className="text-muted-foreground font-light mt-3 max-w-lg">Segui l'avanzamento degli ordini in lavorazione.</p>

        <form onSubmit={track} className="mt-8 flex flex-wrap gap-3 items-end max-w-md">
          <div className="flex-1 min-w-[200px]">
            <label className="text-sm text-muted-foreground mb-2 block">Cerca il tuo ordine con il codice</label>
            <Input data-testid="track-code-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Es. AC-ABC12" className="bg-input border-white/10 text-white rounded-sm uppercase" />
          </div>
          <Button data-testid="track-btn" type="submit" disabled={tracking} className="h-10 bg-neon-cyan text-black hover:bg-white rounded-sm font-semibold gap-2">
            {tracking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Cerca
          </Button>
        </form>
        {trackErr && <p data-testid="track-error" className="text-neon-pink font-mono text-sm mt-3">{trackErr}</p>}
        {tracked && (
          <div data-testid="track-result" className="mt-5 p-5 bg-card border border-neon-cyan/40 rounded-sm">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-mono" style={{ color: "#FFE600" }}>#{tracked.order_code}</span>
              <h3 className="font-display font-bold text-lg text-white">{tracked.title}</h3>
              <StatusBadge status={tracked.status} testId="track-status-badge" />
            </div>
            <p className="font-mono text-xs text-muted-foreground uppercase tracking-wider mt-2">{tracked.service_type || "Stampa 3D"} · {tracked.client_name}</p>
            <StatusStepper current={tracked.status} />
            {tracked.notes && <p className="text-sm text-muted-foreground font-light mt-3">Note: {tracked.notes}</p>}
            {tracked.expected_delivery && <p data-testid="track-delivery" className="text-sm font-light mt-2" style={{ color: "#FFE600" }}>📅 Consegna prevista: {new Date(tracked.expected_delivery).toLocaleDateString("it-IT")}</p>}
            {tracked.status_history?.length > 0 && (
              <div data-testid="track-timeline" className="mt-5">
                <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-3">Avanzamento lavoro</p>
                <ol className="relative border-l border-white/15 ml-1.5">
                  {tracked.status_history.map((h, k) => {
                    const col = STATUS_COLORS[h.status] || "#00F0FF";
                    const isLast = k === tracked.status_history.length - 1;
                    return (
                      <li key={k} data-testid={`track-timeline-item-${k}`} className="ml-5 pb-5 last:pb-0">
                        <span className="absolute -left-[7px] w-3.5 h-3.5 rounded-full border-2 border-background"
                          style={{ backgroundColor: col, boxShadow: isLast ? `0 0 10px ${col}` : "none" }} />
                        <p className="text-sm font-semibold" style={{ color: isLast ? col : "#F8F9FA" }}>{h.status}</p>
                        <p className="font-mono text-[11px] text-muted-foreground mt-0.5">{fmtDate(h.at)}</p>
                      </li>
                    );
                  })}
                </ol>
              </div>
            )}
            {tracked.progress_photos?.length > 0 && (
              <div className="mt-4">
                <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2">Foto avanzamento</p>
                <div className="flex gap-2 flex-wrap">
                  {tracked.progress_photos.map((p, k) => (
                    <img key={k} data-testid={`track-photo-${k}`} src={fileUrl(p)} alt="" className="w-20 h-20 object-cover rounded-sm border border-white/10" />
                  ))}
                </div>
              </div>
            )}
            {tracked.status === "In attesa" ? (
              <div data-testid="cancel-section" className="mt-4 p-3.5 rounded-sm border border-neon-pink/30 bg-neon-pink/5">
                <p className="text-xs text-muted-foreground font-light mb-3 leading-relaxed">
                  Puoi annullare l'ordine finché è <b className="text-white">In attesa</b>. Una volta passato a <b className="text-white">In lavorazione</b> non sarà più possibile.
                </p>
                <Button data-testid="cancel-order-btn" onClick={cancelOrder} disabled={cancelling}
                  className="bg-neon-pink text-white hover:bg-white hover:text-black rounded-sm font-semibold gap-2 transition-colors duration-200">
                  {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />} Annulla ordine
                </Button>
              </div>
            ) : (
              <p data-testid="cancel-locked-note" className="mt-4 flex items-center gap-2 text-xs font-mono text-muted-foreground border border-white/10 rounded-sm px-3 py-2.5">
                <XCircle className="w-3.5 h-3.5 shrink-0 opacity-60" /> L'ordine è in lavorazione: non è più possibile annullarlo.
              </p>
            )}
            <ClientPushButton orderCode={tracked.order_code} />
          </div>
        )}

        <div className="mt-10 flex items-center justify-between">
          <h2 className="font-display font-bold text-lg text-white">Bacheca lavori</h2>
          <button data-testid="board-order-toggle" onClick={() => setBoardOrder((o) => (o === "asc" ? "desc" : "asc"))} className="text-xs font-mono px-3 py-1.5 border border-white/15 rounded-sm text-muted-foreground hover:text-neon-cyan hover:border-neon-cyan transition-colors duration-200">
            Data: {boardOrder === "asc" ? "crescente ↑" : "decrescente ↓"}
          </button>
        </div>
        <div className="mt-4 space-y-3">
          {loading ? (
            <p className="font-mono text-neon-cyan animate-pulse-glow">Caricamento…</p>
          ) : items.length === 0 ? (
            <div data-testid="status-empty" className="flex flex-col items-center py-20 text-muted-foreground">
              <Activity className="w-12 h-12 mb-3 opacity-40" /><p className="font-light">Nessun lavoro al momento.</p>
            </div>
          ) : (
            items.map((it, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                data-testid={`status-row-${i}`}
                className="flex items-center justify-between gap-4 p-4 sm:p-5 bg-card border border-white/10 rounded-sm hover:border-neon-cyan/40 transition-colors duration-200"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="w-10 h-10 flex items-center justify-center rounded-sm bg-neon-cyan/10 border border-neon-cyan/30 shrink-0">
                    <User className="w-5 h-5 text-neon-cyan" />
                  </span>
                  <div className="min-w-0">
                    <p data-testid={`status-name-${i}`} className="font-display font-bold text-white truncate">{it.client_name}</p>
                    <p className="font-mono text-xs text-muted-foreground uppercase tracking-wider">{it.service_type || "Stampa 3D"}</p>
                  </div>
                </div>
                <StatusBadge status={it.status} testId={`status-badge-${i}`} />
              </motion.div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
